import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NOINDEX_PRIVATE_SEO } from "../../../constants/siteSeo";
import { usePageSeo } from "../../../hooks/usePageSeo";
import GatewayTopNav from "../../../components/dashboard/GatewayTopNav";
import GatewaySidebar from "../../../components/dashboard/GatewaySidebar";
import StorefrontDashboardView from "../../../components/marketplace/dashboard/StorefrontDashboardView";
import "../../Dashboard/treydora-gateway-dashboard.css";
import "./storefront-dashboard.css";
import "../marketplace.css";
import "../../../components/legal/legal-components.css";
import {
  fetchMyMarketplaceSellerContext,
  marketplaceSellerRequiresLogin,
} from "../../../services/marketplaceSellerApi";
import {
  storefrontHeroBackground21x9,
  storefrontParcelTowerArt,
  storefrontBlueCharacterArt,
} from "../../../assets/dashboard/storefront";
import { LEGAL_PATHS } from "../../../legal/legalPaths";

function GatewayStorefrontShell({ children }) {
  return (
    <div className="dashboard-shell dashboard-shell--gateway">
      <GatewayTopNav />
      <div className="dashboard-body dg-gateway-atmosphere">
        <GatewaySidebar />
        <div className="dashboard-workpad">{children}</div>
      </div>
    </div>
  );
}

export default function MyStorePage() {
  usePageSeo({
    ...NOINDEX_PRIVATE_SEO,
    title: "My Storefront | Treydora",
    description: "Seller storefront dashboard preview — not indexed.",
  });
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (marketplaceSellerRequiresLogin()) {
      setCtx(null);
      setErr("");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchMyMarketplaceSellerContext();
      setCtx(data);
      setErr("");
    } catch (e) {
      setErr(e?.message || "Could not load storefront context.");
      setCtx(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const signedIn = !marketplaceSellerRequiresLogin();

  if (loading) {
    const body = (
      <div className="sf-dash">
        <p className="sf-muted">Loading storefront…</p>
      </div>
    );
    return signedIn ? <GatewayStorefrontShell>{body}</GatewayStorefrontShell> : body;
  }

  if (marketplaceSellerRequiresLogin()) {
    return (
      <div className="mp-page mp-page--dash mp-page--re-preview mp-atmosphere">
        <section className="mp-re-hero mp-re-hero--console mp-glass">
          <p className="mp-hero__eyebrow mp-hero__eyebrow--gold">Treydora · Oshar owner deck</p>
          <h1 className="mp-hub-hero__title">My Storefront</h1>
          <p className="mp-hub-hero__sub">
            Your NFT real estate will unlock storefront publishing, product listings, commerce tools, and buyer access
            when marketplace operations go live. Sign in to open the preview hub.
          </p>
          <Link className="mp-btn mp-btn--primary mp-btn--enabled mp-btn--glow mp-btn--portal" to="/dashboard">
            Go to dashboard / sign in
          </Link>
        </section>
      </div>
    );
  }

  const stores = ctx?.stores || [];
  const meta = ctx?.ownershipMeta || {};
  const blocked = meta.code === "OWNERSHIP_VERIFICATION_UNAVAILABLE";

  const consoleBody = (
    <>
      <nav className="td-gateway-legal-strip" aria-label="Storefront legal" style={{ marginBottom: 14 }}>
        <span className="td-gateway-legal-strip__label">Seller covenant</span>
        <Link to={LEGAL_PATHS.marketplaceSellerTerms}>Marketplace Seller Terms</Link>
        <Link to={LEGAL_PATHS.center}>Legal Center</Link>
      </nav>
      <StorefrontDashboardView
        heroArt={storefrontHeroBackground21x9}
        parcelArt={storefrontParcelTowerArt}
        characterArt={storefrontBlueCharacterArt}
        storeCount={stores.length}
        err={err}
        blocked={blocked}
        blockedMessage={meta.message}
      />
    </>
  );

  return <GatewayStorefrontShell>{consoleBody}</GatewayStorefrontShell>;
}
